import { useState, type FormEvent } from 'react';
import type { LeadDto, LeadRating, UpsertLeadNoteRequest } from '@/shared/types/domain';
import { Button } from '@/shared/components/Button';
import { RatingSelect } from './RatingSelect';

interface LeadNotesFormProps {
  lead: LeadDto;
  onSave: (data: UpsertLeadNoteRequest) => Promise<void>;
}

export function LeadNotesForm({ lead, onSave }: LeadNotesFormProps) {
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [rating, setRating] = useState<LeadRating | null>(lead.rating ?? null);
  const [interestedProducts, setInterestedProducts] = useState(lead.interestedProducts ?? '');
  const [followUpDate, setFollowUpDate] = useState(
    lead.followUpDateUtc ? lead.followUpDateUtc.slice(0, 10) : ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSaving(true);

    try {
      await onSave({
        notes: notes.trim() || null,
        rating,
        interestedProducts: interestedProducts.trim() || null,
        followUpDateUtc: followUpDate ? new Date(followUpDate).toISOString() : null
      });
      setSavedAt(Date.now());
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <RatingSelect value={rating} onChange={setRating} />

      <div className="field">
        <label className="field__label" htmlFor="notes">
          Notes
        </label>
        <textarea
          id="notes"
          className="field__input"
          style={{ minHeight: 96, paddingTop: 12, paddingBottom: 12, resize: 'vertical' }}
          maxLength={2000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What did you talk about?"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="products">
          Interested products
        </label>
        <input
          id="products"
          className="field__input"
          maxLength={500}
          value={interestedProducts}
          onChange={(e) => setInterestedProducts(e.target.value)}
          placeholder="e.g. Orthotics, Training"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="followup">
          Follow-up date
        </label>
        <input
          id="followup"
          type="date"
          className="field__input"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
        />
      </div>

      <Button type="submit" isLoading={isSaving} fullWidth>
        {savedAt && !isSaving ? 'Saved ✓' : 'Save'}
      </Button>
    </form>
  );
}
